import { useCallback, useEffect, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import type { PlaceSummary } from '@/api/types/Place';
import { copyText } from '@/utils/clipboard';
import { formatPlaceSms } from '@/utils/placeNote';

// „Vykopírovat celý řádek z Adresáře" straight from the jízda detail (user
// request 2026-09-18: „přímo v detailu jízdy u políčka Nakládka / Vykládka by
// mělo být tlačítko na vykopírování […] jinak by museli zbytečně překlikávat,
// hledat"). Reached from BOTH entry points into the detail — Seznam row click
// and Kalendář row click — because both open the same `OrderEditor`.
//
// PURE IN-MEMORY, BY CONTRACT — the click issues NO request, ever.
// -----------------------------------------------------------------------
// The first cut resolved the Adresář row at click time (autocomplete search →
// `GET /api/places/{id}` → SMS text). The user rejected it outright: „proč se
// volá EP autocomplete, když kliknu na zkopírovat? ty data už by měla být
// načtena v detailu — navíc se mi to točí a nefunguje." Both halves are fair:
// the round-trip put a spinner in front of a clipboard write, and every failure
// mode of the lookup (offline, 403, a renamed row) became a failure mode of
// COPYING.
//
// So the resolution moved to the server (`JizdaResponse.loadingPlaceRecord` /
// `unloadingPlaceRecord`, contract §E) and the detail now arrives with the
// whole `PlaceSummary` — `contact` included — already in hand. This hook is
// what is left: pick the record the caller passes, format it, write it. The
// only promise it awaits is the clipboard's own.
//
// The jízda's place fields stay FREE TEXT (CHR-06 §E — no FK, deliberately), so
// a field CAN have no record: free-typed text („Pole u lesa"), or a place
// renamed/removed in the Adresář since. The click then copies the raw field
// text and says so with a WARNING toast rather than failing — the dispatcher
// gets something pasteable either way.
//
// Wording + timing are reused from `places/usePlaceCopy.ts` (the Adresář page's
// own copy button) via the shared `places:` i18n keys, so the two copy paths
// can never drift apart on what they say.

export const COPIED_FEEDBACK_MS = 2000;

/** Which of the two fields a copy is about. */
export type PlaceFieldType = 'LOADING' | 'UNLOADING';

export interface PlaceRecordCopy {
  /** Field whose copy resolved within the last `COPIED_FEEDBACK_MS`, or `null`. */
  copiedField: PlaceFieldType | null;
  /**
   * Copies `record` (whole row, SMS layout) or — when there is none — the raw
   * `text`. Never fetches; never rejects (failures are toasts).
   */
  copyRecord: (
    field: PlaceFieldType,
    text: string,
    record: PlaceSummary | null,
  ) => Promise<void>;
}

export const usePlaceRecordCopy = (): PlaceRecordCopy => {
  const { t } = useTranslation('orders');
  const { enqueueSnackbar } = useSnackbar();
  const [copiedField, setCopiedField] = useState<PlaceFieldType | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The editor unmounts on close/save while the clipboard promise may still be
  // settling — neither the timer nor the setState may fire into a dead tree.
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, []);

  const copyRecord = useCallback(
    async (
      field: PlaceFieldType,
      text: string,
      record: PlaceSummary | null,
    ): Promise<void> => {
      const raw = text.trim();
      // Nothing to copy — the button is disabled in this state anyway.
      if (raw.length === 0) return;

      // The record wins whenever the server resolved one; the raw text is the
      // free-text / renamed-row fallback.
      const payload = record === null ? raw : formatPlaceSms(record);

      const ok = await copyText(payload);
      if (!mountedRef.current) return;
      if (!ok) {
        enqueueSnackbar(t('places:toast.copyFailed'), { variant: 'error' });
        return;
      }

      if (record === null) {
        enqueueSnackbar(t('editor.copyPlace.notFound'), { variant: 'warning' });
      } else {
        enqueueSnackbar(t('places:toast.copied', { name: record.name }), {
          variant: 'success',
        });
      }

      if (timerRef.current !== null) clearTimeout(timerRef.current);
      setCopiedField(field);
      timerRef.current = setTimeout(() => {
        if (mountedRef.current) setCopiedField(null);
      }, COPIED_FEEDBACK_MS);
    },
    [enqueueSnackbar, t],
  );

  return { copiedField, copyRecord };
};
